import { Fade } from "@mui/material";
import { IPurchasable } from "@smartrr/shared/entities/Purchasable";
import {
  IPurchaseStateWithCustomerRelationship,
  PurchaseStateAndDelivery,
} from "@smartrr/shared/entities/PurchaseState";
import { SellingPlanConfigs } from "@smartrr/shared/entities/SellingPlanGroup";
import { getNextActualDeliveryForCpsGroup } from "@smartrr/shared/utils/getScheduledDeliveries";
import { typedLocalize } from "@smartrr/shared/utils/sharedTranslations/localize";
import React, { useCallback, useMemo } from "react";

import { useSmartrrTheme } from "@customer-app/account/views/OperationAquarius/hooks/useSmartrrTheme";
import { useOrganization } from "@customer-app/account/views/OperationAquarius/utils";
import { useToggleModal } from "@customer-app/state/actionCreators/modal";

import { CancelView } from "../../../components/CancelView";
import { DefaultView } from "../../../components/DefaultView";
import { DelayDateView } from "../../../components/DelayDateView";
import { EditAddressView } from "../../../components/EditAddressView";
import { EditFreqView } from "../../../components/EditFreqView";
import { EditPaymentView } from "../../../components/EditPaymentView";
import { GiftView } from "../../../components/GiftView";
import { PauseView } from "../../../components/PauseView";
import { ResumeView } from "../../../components/ResumeView";
import { SetNextDateView } from "../../../components/SetNextDateView";
import { SkipView } from "../../../components/SkipView";
import { SwapItemView } from "../../../components/SwapItemView";
import { SwapPlanView } from "../../../components/SwapPlanView";
import { ValidViews } from "../../../OrderOptionsModalInner";
import { ViewInfo } from "../../types";
import { useBasicSubscriptionInfo } from "../useBasicSubscriptionInfo";
import { useCancellationReason } from "../useCancellationReason";
import { getDeliveryId } from "@customer-app/state/stores/delivery/DeliveryId";

//TODO: use Provider for View
export const useViewComponent = (
  viewInfo: ViewInfo,
  delivery: PurchaseStateAndDelivery,
  purchasablesForAdding: IPurchasable[],
  setButtonsDisabled: React.Dispatch<React.SetStateAction<boolean>>,
  setIconButtonInactive: () => void,
  initialLineItemId?: string
) => {
  const purchaseState = delivery.purchaseState;

  const {
    cancellationReason,
    setCancellationReason,
    userCancellationReasonText,
    setUserCancellationReasonText,
    saveUserCancellationData,
  } = useCancellationReason(purchaseState);
  const organization = useOrganization();

  const theme = useSmartrrTheme();

  const { sellingPlanOptions, nonHiddenSellingPlanOptions, isPaused, isPrepaid, isSellingPlanDeleted, gift } =
    useBasicSubscriptionInfo(delivery);

  const delayDateAvailable =
    delivery?.date.toDateString() ===
    getNextActualDeliveryForCpsGroup(organization!, [purchaseState])?.[0].toDateString();

  const toggleModal = useToggleModal();

  const returnToManageSubscription = useCallback(
    (initialView?: ValidViews) => {
      toggleModal("MANAGE_SUBSCRIPTIONS", {
        deliveryId: getDeliveryId(delivery),
        purchasablesForAdding,
        generatePurchasables: false,
        initialView,
      });
    },
    [delivery, purchasablesForAdding]
  );

  const openAddonList = useCallback(() => {
    toggleModal("ADDON_LIST", {
      deliveryId: getDeliveryId(delivery),
      purchasables: purchasablesForAdding,
      title: typedLocalize("subscriptions.addToYourNextOrder.modalTitle"),
      configType: SellingPlanConfigs.SUBSCRIPTION,
      onCancel: returnToManageSubscription,
      addItemAsSubscription: true,
      showItemAddingNoticeText: true,
    });
  }, [delivery, purchasablesForAdding, returnToManageSubscription]);

  return useMemo(() => {
    switch (viewInfo.view) {
      case "Default": {
        return (
          <Fade in={viewInfo.view === "Default"}>
            <div>
              <DefaultView
                delivery={delivery}
                openAddonList={openAddonList}
                gift={gift}
                isSellingPlanDeleted={isSellingPlanDeleted || isPaused}
                isPrepaid={isPrepaid}
                setButtonsDisabled={setButtonsDisabled}
                purchasablesForAdding={purchasablesForAdding}
                returnToModal={returnToManageSubscription}
                hideAddons
                inManageSubscriptions
              />
            </div>
          </Fade>
        );
      }
      case "Edit frequency": {
        return (
          <Fade in={viewInfo.view === "Edit frequency"}>
            <div>
              <EditFreqView
                delivery={delivery}
                setIconButtonInactive={setIconButtonInactive}
                sellingPlanOptions={nonHiddenSellingPlanOptions}
                saveUserCancellationData={saveUserCancellationData}
                cancellationReason={cancellationReason}
              />
            </div>
          </Fade>
        );
      }
      case "Set next date": {
        return (
          <Fade in={viewInfo.view === "Set next date"}>
            <div>
              <SetNextDateView
                delivery={delivery}
                setIconButtonInactive={setIconButtonInactive}
                saveUserCancellationData={saveUserCancellationData}
                cancellationReason={cancellationReason}
              />
            </div>
          </Fade>
        );
      }

      case "Skip": {
        return (
          <Fade in={viewInfo.view === "Skip"}>
            <div>
              <SkipView
                setIconButtonInactive={setIconButtonInactive}
                delivery={delivery}
                saveUserCancellationData={saveUserCancellationData}
                cancellationReason={cancellationReason}
              />
            </div>
          </Fade>
        );
      }

      case "Edit payment": {
        return (
          <Fade in={viewInfo.view === "Edit payment"}>
            <div>
              <EditPaymentView
                purchaseState={purchaseState}
                customerRelationship={(purchaseState as IPurchaseStateWithCustomerRelationship).custRel}
              />
            </div>
          </Fade>
        );
      }
      case "Pause": {
        return (
          <Fade in={viewInfo.view === "Pause"}>
            <div>
              <PauseView
                purchaseState={purchaseState}
                setIconButtonInactive={setIconButtonInactive}
                initialView={viewInfo.initial === "Pause"}
                returnToModal={() => {
                  returnToManageSubscription();
                  viewInfo.set("Default");
                }}
              />
            </div>
          </Fade>
        );
      }
      case "Resume": {
        return (
          <Fade in={viewInfo.view === "Resume"}>
            <div>
              <ResumeView purchaseState={purchaseState} setIconButtonInactive={setIconButtonInactive} />
            </div>
          </Fade>
        );
      }
      case "Cancel": {
        return (
          <Fade in={viewInfo.view === "Cancel"}>
            <div>
              <CancelView
                delivery={delivery}
                setCancellationReason={setCancellationReason}
                setIconButtonInactive={setIconButtonInactive}
                setView={viewInfo.set}
                theme={theme}
                saveUserCancellationData={saveUserCancellationData}
                setUserCancellationReasonText={setUserCancellationReasonText}
                userCancellationReasonText={userCancellationReasonText}
              />
            </div>
          </Fade>
        );
      }
      case "Swap Plan": {
        return (
          <Fade in={viewInfo.view === "Swap Plan"}>
            <div>
              <SwapPlanView setView={viewInfo.set} delivery={delivery} sellingPlanOptions={sellingPlanOptions} />
            </div>
          </Fade>
        );
      }
      case "Gift": {
        return (
          <Fade in={viewInfo.view === "Gift"}>
            <div>
              <GiftView
                delivery={delivery}
                onCancel={setIconButtonInactive}
                onConfirm={() => {
                  if (cancellationReason && saveUserCancellationData) {
                    saveUserCancellationData();
                  }
                }}
              />
            </div>
          </Fade>
        );
      }
      case "Swap item": {
        return (
          <Fade in={viewInfo.view === "Swap item"}>
            <div>
              <SwapItemView
                setIconButtonInactive={setIconButtonInactive}
                delivery={delivery}
                saveUserCancellationData={saveUserCancellationData}
                cancellationReason={cancellationReason}
                initialLineItemId={initialLineItemId}
              />
            </div>
          </Fade>
        );
      }
      case "Delay date": {
        return (
          <Fade in={viewInfo.view === "Delay date"}>
            <div>
              <DelayDateView
                setIconButtonInactive={setIconButtonInactive}
                delivery={delivery}
                saveUserCancellationData={saveUserCancellationData}
                cancellationReason={cancellationReason}
              />
            </div>
          </Fade>
        );
      }

      case "Edit address": {
        return (
          <Fade in={viewInfo.view === "Edit address"}>
            <div>
              <EditAddressView
                setIconButtonInactive={setIconButtonInactive}
                delivery={delivery}
                gift={gift}
                saveUserCancellationData={saveUserCancellationData}
                cancellationReason={cancellationReason}
              />
            </div>
          </Fade>
        );
      }

      default: {
        // Should not hit here
        return <div></div>;
      }
    }
  }, [delivery, viewInfo, saveUserCancellationData]);
};
